import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { back } from '@/state/nav';
import { db } from '@/lib/db';
import { animals, nowTick } from '@/state/data';
import { uploadPhoto, createDocument, ingestDocument, photoUrl } from '@/lib/photos';
import { openSheet, closeSheet, showToast } from '@/state/ui';
import { applyActions, validateAction } from '@/agent/proposals';
import { Icon } from '@/components/Icon';
import type { Photo, DocumentRow, Animal } from '@/types';
import type { ProposalAction } from '@/agent/types';

/** Deterministic polaroid rotation by photo id (HANDOFF §6.7). */
function rotationFor(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return ((Math.abs(h) % 41) / 10) * (h % 2 ? 1 : -1); // ±2.0°-ish
}

function SignedImg({ path, alt }: { path: string; alt: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    void photoUrl(path).then(setUrl);
  }, [path]);
  if (!url) return <div class="ph" />;
  return <img src={url} alt={alt} loading="lazy" />;
}

/* ---------- capture flow ---------- */

function CaptureSheet({ file, onDone }: { file: File; onDone: () => void }) {
  const [previewUrl] = useState(() => URL.createObjectURL(file));
  const [kind, setKind] = useState<'animal' | 'document'>('animal');
  const [animalId, setAnimalId] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const [doc, setDoc] = useState<DocumentRow | null>(null);
  const [phase, setPhase] = useState<'form' | 'processing' | 'review' | 'failed'>('form');
  const [checked, setChecked] = useState<boolean[]>([]);

  useEffect(() => () => URL.revokeObjectURL(previewUrl), [previewUrl]);

  const activeAnimals = useMemo(
    () => [...animals.value.values()].filter((a) => a.status === 'active').sort((a, b) => a.name.localeCompare(b.name)),
    []
  ) as Animal[];

  const save = async () => {
    setBusy(true);
    try {
      const photo = await uploadPhoto(file, { animalId, caption: caption.trim() || null, kind });
      if (kind === 'animal') {
        showToast({ kind: 'success', message: 'Added to the photo log' });
        closeSheet();
        onDone();
        return;
      }
      // paper record -> OCR
      setPhase('processing');
      const d = await createDocument(photo.id);
      const extracted = await ingestDocument(d.id);
      if (extracted.status !== 'extracted') throw new Error(extracted.error ?? 'extraction failed');
      const ex = extracted.extraction as { actions?: ProposalAction[] };
      setDoc(extracted);
      setChecked((ex.actions ?? []).map((a) => validateAction(a).ok));
      setPhase('review');
    } catch (e) {
      console.warn(e);
      setPhase(kind === 'document' ? 'failed' : 'form');
      if (kind === 'animal') showToast({ kind: 'info', message: `Upload failed: ${(e as Error).message}` });
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!doc) return;
    const ex = doc.extraction as { actions?: ProposalAction[] };
    const actions = ex.actions ?? [];
    const validChecked = actions
      .map((_, i) => i)
      .filter((i) => checked[i] && validateAction(actions[i]).ok);
    setBusy(true);
    try {
      const result = await applyActions(actions, validChecked);
      await db.documents.put({ ...doc, status: 'confirmed', confirmed_at: new Date().toISOString() });
      const { supabase } = await import('@/lib/supabase');
      await supabase.from('documents').update({ status: 'confirmed', confirmed_at: new Date().toISOString() }).eq('id', doc.id);
      showToast({ kind: 'success', message: `Digitized — ${result.applied.length} record${result.applied.length === 1 ? '' : 's'} added` });
      closeSheet();
      onDone();
    } catch (e) {
      showToast({ kind: 'info', message: `Couldn't apply: ${(e as Error).message}` });
    } finally {
      setBusy(false);
    }
  };

  if (phase === 'processing') {
    return (
      <div>
        <img class="cap-preview" src={previewUrl} alt="Scanned record" />
        <div class="doc-spinner">
          <span class="arc" />
          Reading the page…
        </div>
      </div>
    );
  }

  if (phase === 'failed') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-3)' }}>
        <img class="cap-preview" src={previewUrl} alt="Scanned record" />
        <p class="t-small" style={{ margin: 0 }}>
          Couldn't read this one — the photo is saved, so you can try again later with better light, or add the
          records by telling Tend in chat.
        </p>
        <button class="btn btn--block" onClick={() => { closeSheet(); onDone(); }}>OK</button>
      </div>
    );
  }

  if (phase === 'review' && doc) {
    const ex = doc.extraction as { summary?: string; confidence?: string; actions?: ProposalAction[] };
    const actions = ex.actions ?? [];
    return (
      <div>
        <p class="doc-review__summary">
          {ex.summary ?? 'Here’s what I read.'}
          <span class={`pill doc-review__conf ${ex.confidence === 'high' ? 'pill--accent' : ex.confidence === 'low' ? 'pill--danger' : 'pill--warn'}`}>
            {ex.confidence ?? 'medium'} confidence
          </span>
        </p>
        <div class="pcard" style={{ width: '100%', animation: 'none' }}>
          {actions.map((a, i) => {
            const v = validateAction(a);
            return (
              <button
                key={i}
                class="pcard__row"
                disabled={!v.ok}
                onClick={() => v.ok && setChecked((c) => c.map((x, j) => (j === i ? !x : x)))}
              >
                <span class={`cb ${checked[i] && v.ok ? '' : 'cb--off'} ${!v.ok ? 'cb--invalid' : ''}`}>
                  {v.ok ? <Icon name="check" size={14} /> : <Icon name="x" size={12} />}
                </span>
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <div style={{ fontWeight: 600 }}>{a.label}</div>
                  {(a.detail || !v.ok) && <div class="pcard__detail">{v.ok ? a.detail : `Can't apply — ${v.error}`}</div>}
                </div>
              </button>
            );
          })}
          {!actions.length && <p class="t-small" style={{ padding: '12px 14px' }}>No structured records found — saved as a photo only.</p>}
          <div class="pcard__ft">
            <button class="btn btn--ghost btn--sm" onClick={() => { closeSheet(); onDone(); }}>Skip</button>
            <button class="btn btn--sm" disabled={busy || !checked.some(Boolean)} onClick={confirm}>
              Add to Tend
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-3)' }}>
      <img class="cap-preview" src={previewUrl} alt="Captured" />
      <div class="cap-kind">
        <button class={`seg ${kind === 'animal' ? 'is-on' : ''}`} onClick={() => setKind('animal')}>
          Animal photo
        </button>
        <button class={`seg ${kind === 'document' ? 'is-on' : ''}`} onClick={() => setKind('document')}>
          Paper record
        </button>
      </div>
      {kind === 'animal' && (
        <>
          <div class="cap-tags">
            <button class={`pill ${animalId === null ? 'pill--accent' : ''}`} onClick={() => setAnimalId(null)}>
              No tag
            </button>
            {activeAnimals.map((a) => (
              <button key={a.id} class={`pill ${animalId === a.id ? 'pill--accent' : ''}`} onClick={() => setAnimalId(a.id)}>
                {a.name}
              </button>
            ))}
          </div>
          <input
            class="input"
            id="cap-caption"
            placeholder="Caption — “ate full 60ml”"
            value={caption}
            onInput={(e) => setCaption((e.target as HTMLInputElement).value)}
          />
        </>
      )}
      {kind === 'document' && (
        <p class="t-small" style={{ margin: 0 }}>
          I'll read the page and turn it into animals, schedules and history — you approve everything before it's saved.
        </p>
      )}
      <button class="btn btn--block" disabled={busy} onClick={save}>
        {busy ? 'Uploading…' : kind === 'document' ? 'Scan it' : 'Save photo'}
      </button>
    </div>
  );
}

/* ---------- main screen ---------- */

export function PhotoLogScreen() {
  void nowTick.value;
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [filter, setFilter] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    void db.photos.orderBy('taken_at').reverse().limit(300).toArray().then(setPhotos);
  }, [refresh, nowTick.value]);

  const days = useMemo(() => {
    const filtered = filter ? photos.filter((p) => p.animal_id === filter) : photos;
    const byDay = new Map<string, Photo[]>();
    for (const p of filtered) {
      const day = p.taken_at.slice(0, 10);
      if (!byDay.has(day)) byDay.set(day, []);
      byDay.get(day)!.push(p);
    }
    return [...byDay.entries()];
  }, [photos, filter]);

  const onFile = (e: Event) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    (e.target as HTMLInputElement).value = '';
    if (!file) return;
    openSheet({ title: 'New photo', content: <CaptureSheet file={file} onDone={() => setRefresh((r) => r + 1)} /> });
  };

  const fmtDay = (d: string) => {
    const today = new Date().toISOString().slice(0, 10);
    const yest = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
    const date = new Date(d + 'T12:00');
    const label = date.toLocaleDateString([], { month: 'long', day: 'numeric' });
    if (d === today) return `Today · ${label}`;
    if (d === yest) return `Yesterday · ${label}`;
    return `${date.toLocaleDateString([], { weekday: 'short' })} · ${label}`;
  };

  const tagFor = (p: Photo) => {
    const a = p.animal_id ? animals.value.get(p.animal_id) : null;
    const t = new Date(p.taken_at);
    const time = `${t.getHours() % 12 === 0 ? 12 : t.getHours() % 12}${t.getHours() < 12 ? 'a' : 'p'}`;
    return a ? `${a.name} · ${time}` : time;
  };

  const filterName = filter ? animals.value.get(filter)?.name : null;

  const cycleFilter = () => {
    const withPhotos = [...new Set(photos.map((p) => p.animal_id).filter(Boolean))] as string[];
    if (!withPhotos.length) return;
    if (filter === null) setFilter(withPhotos[0]);
    else {
      const idx = withPhotos.indexOf(filter);
      setFilter(idx === -1 || idx === withPhotos.length - 1 ? null : withPhotos[idx + 1]);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div class="screen screen--no-tabbar screen--fade-in">
      <header class="header header--sub">
        <button class="iconbtn" aria-label="Back" onClick={() => back('/more')}>
          <Icon name="chevron-left" size={20} />
        </button>
        <div>
          <div class="header__title">Photo log</div>
        </div>
        <div class="header__spacer" />
        <button class="btn btn--secondary btn--sm" onClick={cycleFilter}>
          {filterName ?? 'All animals'}
        </button>
      </header>

      <div class="plog-scroll">
        {days.length === 0 && (
          <div class="now-empty">
            <Icon name="photo" size={48} class="now-empty__art" />
            <h2 class="now-empty__title">No photos yet</h2>
            <p class="now-empty__sub">Tap the camera to start the visual diary — or scan a paper record to digitize it.</p>
          </div>
        )}
        {days.map(([day, list]) => (
          <div key={day}>
            <div class="day-h">
              <div>
                <p class="day-l">{fmtDay(day)}</p>
              </div>
              <span class="t-meta">{list.length} photo{list.length === 1 ? '' : 's'}</span>
            </div>
            {day === today && list.length <= 3 ? (
              <div class="pol-stack">
                {list.map((p, i) => (
                  <div
                    key={p.id}
                    class="pol"
                    style={{
                      transform: `rotate(${rotationFor(p.id)}deg)`,
                      left: i === 0 ? '14px' : i === 1 ? '96px' : undefined,
                      right: i === 2 ? '18px' : undefined,
                      top: `${[18, 8, 32][i]}px`,
                      zIndex: [2, 3, 1][i],
                    }}
                  >
                    <SignedImg path={p.storage_path} alt={p.caption ?? 'photo'} />
                    <div class="cap">{[tagFor(p), p.caption].filter(Boolean).join(' · ')}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div class="row3">
                {list.map((p) => (
                  <div key={p.id} class="thumb">
                    <SignedImg path={p.storage_path} alt={p.caption ?? 'photo'} />
                    {p.kind === 'document' && (
                      <span class="doc-badge">
                        <Icon name="doc" />
                      </span>
                    )}
                    <span class="tag">{tagFor(p)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        <div style={{ height: '90px' }} />
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={onFile}
      />
      <button class="cam-fab" style={{ bottom: 'calc(var(--safe-bottom) + 18px)' }} aria-label="Take photo" onClick={() => fileRef.current?.click()}>
        <Icon name="camera" size={22} />
      </button>
    </div>
  );
}
